import './ConfigureTaskForm.css';
import * as React from 'react';
import { DefaultButton, PanelType, PrimaryButton, SearchBox } from 'office-ui-fabric-react';
import * as Metadata from '../../../entities/Metadata';
import { EntityType } from '../../../entities/common';
import { arraysEqual, notUndefined } from '../../utils/common';
import ExpandablePanel from '../../common/ExpandablePanel';
import GroupedFieldsConfiguration from '../../field/ConfigureFields/GroupedFieldsConfiguration';
import FieldComponent from '../../common/sectionsControl/uiControls/fieldsArea/FieldComponent';
import { DisplayFieldService } from '../../common/DisplayFieldService';
import ErrorInput from '../../common/inputs/ErrorInput';
import TextInput from '../../common/inputs/TextInput';
import { IInputProps } from '../../common/interfaces/IInputProps';

type Props = {
    fields: Metadata.Field[];
    fieldNames: string[];
    onSave: (fieldNames: string[]) => void;
    onDismiss: () => void;
};

const MANDATORY_FIELDS = ['Name'];

const PREVIEW_STATE = { attributes: {} };

const ConfigureTaskFormPanel = (props: Props) => {
    const { fields, fieldNames, onSave, onDismiss } = props;

    const fieldsByName = React.useMemo(() => Metadata.toMap(fields), [fields]);
    const [selected, setSelected] = React.useState<string[]>(fieldNames);
    const [filter, setFilter] = React.useState('');

    const previewFields = React.useMemo(
        () => selected.map(_ => fieldsByName[_]).filter(notUndefined),
        [selected, fieldsByName]);

    const isDirty = !arraysEqual(selected, fieldNames);

    const onFilterChange = React.useCallback((_: any, value?: string) => setFilter(value || ''), []);
    const clearFilter = React.useCallback(() => setFilter(''), []);
    const onEditField = React.useCallback(() => { }, []);
    const onSelectionChange = React.useCallback((_: { column1: string[] }) => setSelected(_.column1), []);

    const onRenderHeader = React.useCallback(() => <div className="ms-Panel-header">
        <p className="ms-Panel-headerText">Configure Task Form</p>
        <div className="ms-Panel-secondaryText">
            Select the fields to be displayed in the task form. Drag and drop to reorder.
        </div>
    </div>, []);

    const onRenderFooterContent = React.useCallback(() => <div className="configure-task-form-commands">
        <PrimaryButton text="Save" disabled={!isDirty} onClick={() => onSave(selected)} />
        <DefaultButton text="Cancel" onClick={onDismiss} />
    </div>, [isDirty, selected, onSave, onDismiss]);

    return <ExpandablePanel
        className="configure-task-form"
        isOpen
        isLightDismiss
        type={PanelType.custom}
        customWidth="1000px"
        onRenderHeader={onRenderHeader}
        onRenderFooterContent={onRenderFooterContent}
        onDismiss={onDismiss}>
        <div className="configure-task-form-body">
            <div className="configure-task-form-preview panel-area">
                {previewFields.map(_ => <FieldComponent
                    key={_.name}
                    field={_}
                    state={PREVIEW_STATE}
                    entityType={EntityType.Task}
                    className={`grid-item ${MANDATORY_FIELDS.includes(_.name) ? 'preview-full-width ' : ''}${_.settings?.['views']?.['details']?.['className'] ?? ''}`}
                    renderInput={renderPreviewInput}
                    isConfigureMode
                    disabled
                />)}
            </div>
            <div className="configure-task-form-fields configure-fields">
                <SearchBox
                    value={filter}
                    placeholder="Search"
                    onChange={onFilterChange}
                    onClear={clearFilter}
                    onEscape={clearFilter}
                />
                <GroupedFieldsConfiguration
                    fields={fields}
                    selected={{ column1: selected, column2: [] }}
                    onChange={onSelectionChange}
                    flattenFieldsInThisViewGroups
                    hideFieldsInThisViewTitle
                    entityType={EntityType.Task}
                    filter={filter}
                    mandatoryFields={MANDATORY_FIELDS}
                    onEditField={onEditField}
                    onDismiss={onDismiss}
                />
            </div>
        </div>
    </ExpandablePanel>;
}

function renderPreviewInput(inputProps: IInputProps, state: any, field: Metadata.Field): JSX.Element {
    const element = DisplayFieldService.buildFieldInputElement(field, inputProps, EntityType.Task, state);
    return element?.type === ErrorInput ? <TextInput {...inputProps} disabled /> : element;
}

export default ConfigureTaskFormPanel;
